import { useRef } from "react";

/**
 * What can still be taken back, as iOS's Undo bar: over the floating Scan cards on a phone, at the bottom from 640 px,
 * where Scan cards is in the page's head.
 * The title gives way before the change it names, as iOS cuts the line in the middle.
 * Its time stops while it has the pointer or the focus, so a keyboard has time to reach it.
 */
export function UndoToast({
  title,
  change,
  onUndo,
  onHold,
  onRelease,
}: {
  title: string;
  change: string;
  onUndo: () => void;
  onHold: () => void;
  onRelease: () => void;
}) {
  const on = useRef({ pointer: false, focus: false });
  const set = (what: "pointer" | "focus", value: boolean) => {
    const before = on.current.pointer || on.current.focus;
    on.current = { ...on.current, [what]: value };
    const after = on.current.pointer || on.current.focus;
    if (after && !before) onHold();
    if (before && !after) onRelease();
  };
  return (
    <div
      data-floating="undo"
      className="pointer-events-none fixed inset-x-0 bottom-[calc(var(--tab-bar,0px)+5.5rem)] z-30 px-4 sm:bottom-6 sm:left-52 sm:px-8"
    >
      <div
        role="status"
        onPointerEnter={() => set("pointer", true)}
        onPointerLeave={() => set("pointer", false)}
        onFocus={() => set("focus", true)}
        onBlur={() => set("focus", false)}
        className="pointer-events-auto mx-auto flex max-w-md items-center gap-2.5 rounded-row border border-line bg-app-elevated px-3.5 py-3 shadow-2xl"
      >
        <p className="flex min-w-0 flex-1 text-[13px] text-white">
          <span className="truncate">{title}</span>
          <span className="shrink-0 whitespace-pre"> · {change}</span>
        </p>
        <button
          type="button"
          onClick={onUndo}
          aria-keyshortcuts="Control+Z Meta+Z"
          className="shrink-0 text-sm font-semibold text-accent"
        >
          Undo
        </button>
      </div>
    </div>
  );
}
